import { useEffect, useState } from "react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { FireSimulation } from "../../../types/FireSimulation";
import { MetricItem } from "../../../types/storyMap";
import { useUnitSystem } from "../../../hooks/useUnitSystem";
import { toDisplayValueUnit } from "../../../utils/units";
import { entryProjectId } from "../layerEntries";
import type { ScopeLists } from "../useScopeLists";
import type { StoryScopeInfo } from "../useStoryScope";
import ProjectSelect from "./ProjectSelect";

export interface SimKpi {
  value: number | string;
  units: string;
  label: string;
  icon?: string;
}

/** A simulation's KPI list, tolerating the legacy object shape. */
const simKpiList = (sim: FireSimulation | undefined): SimKpi[] =>
  sim && Array.isArray(sim.kpis) ? (sim.kpis as SimKpi[]) : [];

interface MetricItemFieldsProps {
  item: MetricItem;
  onChange: (patch: Partial<MetricItem>) => void;
  scope: StoryScopeInfo;
  lists: ScopeLists;
}

const NO_SIMS: FireSimulation[] = [];

/**
 * label/value/units fields plus the optional "link to simulation KPI" picker,
 * shared by the metric block and metrics-row items. Folder and
 * organisation-wide stories pick the simulation's project first.
 */
export default function MetricItemFields({
  item,
  onChange,
  scope,
  lists,
}: MetricItemFieldsProps) {
  const unitSystem = useUnitSystem();
  const linkedSimId = item.source.kind === "sim_kpi" ? item.source.simulationId : "";
  const linkedKpiLabel = item.source.kind === "sim_kpi" ? item.source.kpiLabel : "";
  // The project the KPI comes from: the source's, else (while nothing is
  // linked yet) the one picked in this editor.
  const linkedProjectId = entryProjectId(item.source.kind === "sim_kpi" ? item.source : null, scope.defaultProjectId);
  const [pickedProjectId, setPickedProjectId] = useState<number | null>(linkedProjectId);
  const projectId = linkedProjectId ?? pickedProjectId;
  useEffect(() => {
    if (projectId !== null) lists.requestProject(projectId);
  }, [projectId, lists]);
  const simulations = projectId !== null ? lists.simulationsByProject[String(projectId)] ?? NO_SIMS : NO_SIMS;
  const linkedSim = simulations.find((s) => s.simulation_id === linkedSimId);
  const kpis = simKpiList(linkedSim);

  const handleSimChange = (simulationId: string) => {
    if (!simulationId) {
      onChange({ source: { kind: "manual" } });
      return;
    }
    onChange({
      source: {
        kind: "sim_kpi",
        simulationId,
        kpiLabel: "",
        ...(scope.isScoped && projectId !== null ? { projectId } : {}),
      },
    });
  };

  // Story-display rounding, mirroring the publish freeze's default format:
  // >=100 → integer, >=10 → 1 dp, else 2 dp, trailing zeros stripped.
  const formatKpiValue = (value: unknown): string => {
    const num = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(num)) return value == null ? "" : String(value);
    const magnitude = Math.abs(num);
    const decimals = magnitude >= 100 ? 0 : magnitude >= 10 ? 1 : 2;
    const text = num.toFixed(decimals);
    return text.includes(".") ? text.replace(/\.?0+$/, "") : text;
  };

  // KPI values arrive metric; fill the fields in the author's unit system.
  // The publish freeze converts the fresh KPI the same way, so the authored
  // display value still matches what freezing resolves.
  const displayKpi = (kpi: SimKpi): { value: string; units: string } => {
    const num = typeof kpi.value === "number" ? kpi.value : Number(kpi.value);
    if (!Number.isFinite(num)) {
      return { value: formatKpiValue(kpi.value), units: kpi.units ?? "" };
    }
    const display = toDisplayValueUnit(num, kpi.units, unitSystem);
    return { value: formatKpiValue(display.value), units: display.unit };
  };

  const handleKpiChange = (kpiLabel: string) => {
    if (!linkedSimId) return;
    const kpi = kpis.find((k) => k.label === kpiLabel);
    onChange({
      source: {
        kind: "sim_kpi",
        simulationId: linkedSimId,
        kpiLabel,
        ...(scope.isScoped && projectId !== null ? { projectId } : {}),
      },
      ...(kpi
        ? {
            label: item.label || kpi.label,
            ...displayKpi(kpi),
          }
        : {}),
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          label="Label"
          size="small"
          fullWidth
          value={item.label}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          label="Value"
          size="small"
          fullWidth
          value={item.value}
          onChange={(e) => onChange({ value: e.target.value })}
        />
        <TextField
          label="Units"
          size="small"
          sx={{ width: 140 }}
          value={item.units}
          onChange={(e) => onChange({ units: e.target.value })}
        />
      </Box>
      <ProjectSelect
        scope={scope}
        value={projectId}
        onChange={(next) => {
          setPickedProjectId(next);
          // A KPI belongs to a simulation, which belongs to a project.
          if (item.source.kind === "sim_kpi") onChange({ source: { kind: "manual" } });
        }}
      />
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          select
          label="Link to simulation KPI"
          size="small"
          fullWidth
          value={linkedSimId}
          onChange={(e) => handleSimChange(e.target.value)}
        >
          <MenuItem value="">Manual value</MenuItem>
          {simulations.map((sim) => (
            <MenuItem key={sim.simulation_id} value={sim.simulation_id}>
              {sim.name || "Unnamed simulation"}
            </MenuItem>
          ))}
        </TextField>
        {linkedSimId && (
          <TextField
            select
            label="KPI"
            size="small"
            fullWidth
            value={linkedKpiLabel}
            onChange={(e) => handleKpiChange(e.target.value)}
          >
            {kpis.map((kpi) => (
              <MenuItem key={kpi.label} value={kpi.label}>
                {kpi.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>
      {item.source.kind === "sim_kpi" && (
        <Typography variant="caption" sx={{ color: "#00d4ad" }}>
          live metric — freezes at publish
        </Typography>
      )}
    </Box>
  );
}
